import { makeStyles, mergeClasses, shorthands, tokens } from "@fluentui/react-components";
import type { CSSProperties, ReactNode } from "react";

const useStyles = makeStyles({
  root: {
    display: "flex",
    flexDirection: "column",
    justifyContent: "space-between",
    position: "relative",
    padding: "8px 12px",
    backgroundColor: tokens.colorNeutralBackground1,
    borderRadius: tokens.borderRadiusMedium,
    ...shorthands.border("1px", "solid", tokens.colorNeutralStroke2),
    minHeight: "56px",
    boxSizing: "border-box",
    overflow: "hidden",
    transitionProperty: "background-color, border-color, box-shadow",
    transitionDuration: "0.15s",
    transitionTimingFunction: "ease",
  },
  interactive: {
    cursor: "pointer",
    userSelect: "none",
    ":hover": {
      backgroundColor: tokens.colorNeutralBackground1Hover,
      ...shorthands.borderColor(tokens.colorBrandStroke1),
    },
    ":active": {
      backgroundColor: tokens.colorNeutralBackground1Pressed,
    },
    ":focus-visible": {
      outline: `2px solid ${tokens.colorStrokeFocus2}`,
      outlineOffset: "1px",
    },
  },
  topRow: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: tokens.spacingHorizontalS,
    minHeight: "16px",
    minWidth: 0,
  },
  label: {
    fontSize: tokens.fontSizeBase200, // 12px
    lineHeight: tokens.lineHeightBase200,
    color: tokens.colorNeutralForeground2,
    fontWeight: tokens.fontWeightRegular,
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
    flexShrink: 1,
  },
  hint: {
    fontSize: tokens.fontSizeBase100, // 10-11px
    lineHeight: tokens.lineHeightBase100,
    color: tokens.colorNeutralForeground3,
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
    flexShrink: 0,
  },
  bottomRow: {
    display: "flex",
    alignItems: "baseline",
    gap: tokens.spacingHorizontalXS,
    marginTop: "2px",
    minWidth: 0,
  },
  value: {
    fontSize: tokens.fontSizeBase500, // 20px
    lineHeight: tokens.lineHeightBase500, // 24px
    fontWeight: tokens.fontWeightRegular,
    fontVariantNumeric: "tabular-nums",
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
    letterSpacing: "-0.01em",
  },
  unit: {
    fontSize: tokens.fontSizeBase200,
    color: tokens.colorNeutralForeground3,
    whiteSpace: "nowrap",
    flexShrink: 0,
  },
  /* 상태 톤 (Text Colors) */
  danger: {
    color: tokens.colorPaletteRedForeground1,
  },
  warning: {
    color: tokens.colorPaletteDarkOrangeForeground1,
  },
  success: {
    color: tokens.colorPaletteGreenForeground1,
  },
  brand: {
    color: tokens.colorBrandForeground1,
  },
  muted: {
    color: tokens.colorNeutralForeground3,
  },
});

export interface CompactKpiProps {
  /** 1행 좌측: 지표명 / 라벨 */
  label: ReactNode;
  /** 2행 좌측: 메인 핵심 수치 */
  value: ReactNode;
  /** 1행 우측: 보조 설명 / 상태 태그 / 변동폭 */
  hint?: ReactNode;
  /** 2행 우측: 단위 또는 인라인 보조 텍스트 (예: M/M, 개, 명) */
  unit?: ReactNode;
  /** 수치 컬러 톤 */
  tone?: "danger" | "warning" | "success" | "brand" | "muted";
  /** 클릭 핸들러 (지정 시 인터랙티브 칩 스타일 및 키보드 접근성 활성화) */
  onClick?: () => void;
  className?: string;
  style?: CSSProperties;
  ariaLabel?: string;
}

/**
 * 2줄 컴팩트 KPI 메트릭 카드
 * - 1행: 라벨 (좌측) + 힌트/상태 (우측)
 * - 2행: 핵심 수치 (좌측) + 단위/부가정보 (우측)
 * - 높이: 약 56px 규격
 * - 박스 하단 게이지 바는 두지 않는다. 계획 대비 진척·분포는 `@/components/metric-panel`을 쓴다.
 */
export function CompactKpi({
  label,
  value,
  hint,
  unit,
  tone,
  onClick,
  className,
  style,
  ariaLabel,
}: CompactKpiProps) {
  const styles = useStyles();

  return (
    <div
      role={onClick ? "button" : "group"}
      tabIndex={onClick ? 0 : undefined}
      onClick={onClick}
      onKeyDown={
        onClick
          ? (e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                onClick();
              }
            }
          : undefined
      }
      className={mergeClasses(
        styles.root,
        onClick && styles.interactive,
        className,
      )}
      style={style}
      aria-label={
        ariaLabel ||
        (typeof label === "string" && (typeof value === "string" || typeof value === "number")
          ? `${label}: ${value}`
          : undefined)
      }
    >
      {/* 1행: 지표 라벨 + 우측 힌트 */}
      <div className={styles.topRow}>
        <div className={styles.label}>{label}</div>
        {hint !== undefined && hint !== null && (
          <div className={styles.hint}>{hint}</div>
        )}
      </div>

      {/* 2행: 메인 수치 + 인라인 단위 */}
      <div className={styles.bottomRow}>
        <span
          className={mergeClasses(
            styles.value,
            tone === "danger" && styles.danger,
            tone === "warning" && styles.warning,
            tone === "success" && styles.success,
            tone === "brand" && styles.brand,
            tone === "muted" && styles.muted,
          )}
        >
          {value}
        </span>
        {unit !== undefined && unit !== null && (
          <span className={styles.unit}>{unit}</span>
        )}
      </div>
    </div>
  );
}
